import { useState, useRef, useCallback, useEffect, forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import { borderRadii, scrollbarCss } from 'app/spacing';
import { TextSpan } from 'app/typography';
import { useLength } from 'hooks/useLength';
import BaseInput from '../TextInput/BaseInput';
import BaseInputLayout from '../TextInput/BaseInputLayout';
import { hasInputControl, MaybeInputControl } from '../InputControl';
import ResizeWrapper from '../ResizeWrapper';
import { applyRef } from '../props';
import Validation, { useValidation } from '../Validation';
import { useKeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { useUrlFromValue } from '../helpers';
import { Button } from '@kizen/kds/Button';
import { TextEllipsisWithTooltip } from '@kizen/page-builder/internal/components/Kizen/Table';
import { Spacer } from '@kizen/kds/Spacer';
import { useTooltip } from 'components/Kizen/Tooltip';

const DEFAULT_INITIAL_HEIGHT = 63;
const CRAMPED_HEIGHT = 38;

const TooltipDiv = styled.div`
  position: absolute;
  margin-top: 15px;
  padding-bottom: 25px;
  top: 0;
  left: 0;
  width: 100%;
  pointer-events: none;
  ${({ width }) =>
    width &&
    css`
      width: ${width}px;
    `}
  ${({ height }) =>
    height &&
    css`
      height: ${height}px;
    `}
`;

const OutlineTextarea = styled(BaseInput)`
  ${scrollbarCss}
  height: 100%;
  ${({ disabled, fullHeight, initialHeight }) =>
    disabled && fullHeight
      ? css`
          min-height: ${initialHeight}px;
          height: 100%;
        `
      : css``}
  /* Account for baseline to get accurate spacing */
  /* Account for right scrollbar width */
  ${({ intialHeight }) =>
    (intialHeight || DEFAULT_INITIAL_HEIGHT) < CRAMPED_HEIGHT
      ? css`
          padding-top: 9px;
          padding-bottom: 9px;
        `
      : css`
          padding-top: 10px;
          padding-bottom: 10px;
        `}

  padding-right: 6px;
  padding-left: 10px;
  overflow-y: scroll;
  width: 100%;
  line-height: 1.15;
  resize: none;
`;

export const OutlineLayout = styled(BaseInputLayout)`
  border-radius: ${borderRadii.small};
  ${({ fullHeight, fixedHeight }) =>
    fixedHeight
      ? css`
          height: ${fixedHeight}px;
        `
      : fullHeight
        ? css`
            height: 100%;
          `
        : ''}
  border: none;
  // no blue on hover
  ${({ disabled, focused }) =>
    !disabled &&
    !focused &&
    css`
      &:hover {
        border-color: var(--color-border-medium);
      }
    `}
`;

export const MergeColumn = styled.div`
  width: 100%;
  ${({ fullHeight, maxWidth }) => css`
    height: ${fullHeight ? '100%' : null};
    max-width: ${maxWidth || null};
  `}
`;

export const CountWrapper = styled.label`
  display: flex;
  justify-content: space-between;
  margin: 12px 0 5px;
`;

export const StyledLabel = styled(TextSpan)`
  line-height: 125%;
  margin-bottom: 12px;
`;

const ReadOnlyText = styled(TextEllipsisWithTooltip)`
  padding: 0 6px 0px 10px;
  margin: 9px 0 10px;
  height: 32px;
`;

const OutlineLongText = forwardRef((props, ref) => {
  const {
    fieldId,
    className,
    value = '',
    placeholder = null,
    disabled = null,
    error = null,
    onChange = null,
    onFocus = null,
    onBlur = null,
    showCountComponent = false,
    enableResize = true,
    setMenuOpen,
    selectionStart = null,
    setShown,
    handleSubmit,
    fullHeight,
    displayMaskedValue,
    displayUrlLink = false,
    initialHeight = DEFAULT_INITIAL_HEIGHT,
    fixedHeight,
    minWidth = 230,
    tooltipLabel,
    tooltipMaxWdith,
    ...others
  } = props;
  const { t } = useTranslation();
  const { assignFieldHandle } = useKeyBoardContext();
  const [focused, setFocused] = useState(false);
  const validationRef = useRef();
  const textareaElem = useRef(null);
  const wrapperRef = useRef();
  const [, validationProps] = useValidation(validationRef, {
    value,
    validate: props.validate,
  });
  const inputSizingRef = useRef();
  const [inputWidth, setInputWidth] = useState(0);
  const [inputHeight, setInputHeight] = useState(0);
  const [tooltipProps, tooltip] = useTooltip({
    label: tooltipLabel,
    maxWidth: tooltipMaxWdith,
    popperConfig: {
      modifiers: {
        preventOverflow: {
          enabled: true,
          rootBoundary: 'viewport',
        },
      },
    },
  });

  const mergeRef = useCallback(
    (el) => {
      applyRef(textareaElem, el);
      applyRef(ref, el);
    },
    [ref]
  );

  const [
    {
      maxLength,
      singleMessageCount,
      maxLengthForSingleMessage,
      countOfMessages,
      maxCountOfMessages,
      totalCount,
    },
    updateEvent,
  ] = useLength(0, Infinity);

  useEffect(() => {
    // calculate width for tooltip when input is disabled
    if (tooltipLabel && inputSizingRef.current) {
      setInputWidth(inputSizingRef.current.offsetWidth);
      setInputHeight(inputSizingRef.current.scrollHeight);
    }
  }, [tooltipLabel, value]);

  const textareaChangeEvent = useCallback(
    (text) => {
      if (maxLength >= (text || '').length) {
        if (onChange) {
          onChange(text);
        }
        updateEvent(text);
      } else {
        const truncated = text.substring(0, maxLength);
        // TODO Be more polite here than cutting off any overflow
        if (onChange) {
          onChange(truncated);
        }
        updateEvent(truncated);
      }
    },
    [maxLength, updateEvent, onChange]
  );

  useEffect(() => {
    if (selectionStart !== null && textareaElem && textareaElem.current) {
      textareaElem.current.selectionStart = selectionStart;
      textareaElem.current.selectionEnd = selectionStart;
      textareaElem.current.focus({ preventScroll: true });
    }
  }, [textareaElem, selectionStart]);

  const {
    'data-field-type': _1,
    'data-field-id': _2,
    onBlur: _3,
    onFocus: _4,
    ...forInputControl
  } = props;

  const editRef = useRef(false);

  assignFieldHandle(fieldId, {
    customTab: () => {
      setShown?.(false);
      handleSubmit?.();
      setFocused(false);
    },
    customFocus: () => {
      if (!editRef.current) {
        textareaElem.current?.focus();
        setShown?.(true);
        setFocused(true);
        onFocus();
      }
      return textareaElem.current;
    },
    customEnter: (e) => {
      if (!editRef.current) {
        setFocused(false);
        setShown?.(false);
        return false;
      } else {
        setFocused(!(e.ctrlKey + e.metaKey));
        setShown?.(!(e.ctrlKey + e.metaKey));
        if (e.ctrlKey + e.metaKey) {
          handleSubmit?.();
        }
        return !(e.ctrlKey + e.metaKey);
      }
    },
    customUp: () => {
      if (!editRef.current) {
        textareaElem.current.focus();
      }
    },
    customDown: () => {
      if (!editRef.current) {
        textareaElem.current.focus();
      }
    },
    customSpace: () => {
      textareaElem.current.focus();
    },
    customInput: () => {
      if (!editRef.current) {
        textareaElem.current.focus();
        const caretPosition = textareaElem.current.value?.length || 0;
        textareaElem.current.setSelectionRange(caretPosition, caretPosition);
      }
    },
    disabled,
  });

  const { urlFromValue, urlFromValueLinkProps, linkButtonProps } =
    useUrlFromValue(value, !displayMaskedValue && displayUrlLink);

  const {
    onFocus: tooltipOnFocus,
    onBlur: tooltipOnBlur,
    ...restTooltipProps
  } = tooltipProps;

  return (
    <MaybeInputControl variant="outline" forInput {...forInputControl}>
      <OutlineLayout
        ref={validationRef}
        className={!hasInputControl(props) && className}
        focused={focused}
        disabled={disabled}
        error={error}
        fullHeight={fullHeight}
        fixedHeight={fixedHeight}
        overrideDisabledColor={disabled}
      >
        <MergeColumn
          ref={wrapperRef}
          fullHeight={fullHeight}
          maxWidth={urlFromValue && !disabled ? 'calc(100% - 36px)' : '100%'}
        >
          <ResizeWrapper
            ref={inputSizingRef}
            focused={focused}
            disabled={disabled}
            fullHeight={fullHeight}
            enableResize={enableResize}
            minHeight={initialHeight}
            minWidth={minWidth}
            addBorder={!error}
            error={!!error}
          >
            {tooltipLabel ? tooltip : null}
            {urlFromValue && disabled ? (
              <ReadOnlyText {...urlFromValueLinkProps}>{value}</ReadOnlyText>
            ) : (
              <OutlineTextarea
                focused={focused}
                ref={mergeRef}
                as="textarea"
                value={value}
                onChange={(ev) => {
                  textareaChangeEvent(ev.target.value, ev);
                }}
                placeholder={placeholder}
                disabled={disabled}
                initialHeight={initialHeight}
                onFocus={(ev) => {
                  setFocused(true);
                  editRef.current = true;
                  if (onFocus) onFocus(ev);
                  tooltipOnFocus(ev);
                }}
                onBlur={(ev) => {
                  setFocused(false);
                  editRef.current = false;
                  tooltipOnBlur(ev);
                  if (onBlur) onBlur(ev);
                }}
                enableResize={false}
                {...restTooltipProps}
                {...others}
              />
            )}
            {tooltipLabel ? (
              <TooltipDiv
                {...tooltipProps}
                width={disabled ? inputWidth : null}
                height={inputHeight}
              />
            ) : null}
          </ResizeWrapper>
        </MergeColumn>
        {urlFromValue && !disabled ? (
          <>
            <Spacer size={8} />
            <Button {...linkButtonProps} />
            <Spacer size={15} />
          </>
        ) : null}
      </OutlineLayout>
      <Validation {...validationProps} />
      {showCountComponent && (
        <CountWrapper>
          <StyledLabel>
            {`${singleMessageCount}/${maxLengthForSingleMessage} ${t(
              'characters left in message'
            )} ${countOfMessages} ${t('of')} ${maxCountOfMessages}`}
          </StyledLabel>
          <StyledLabel>
            {`${totalCount} ${t('of')} ${maxLength} ${t('total characters')}`}
          </StyledLabel>
        </CountWrapper>
      )}
    </MaybeInputControl>
  );
});

OutlineLongText.displayName = 'OutlineLongText';

export default OutlineLongText;
